import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { JOBS_KEY } from './useJobs';
import type { ScrapeLead, ScrapeLogEntry, ScrapeParams, StreamStatus } from '../types';
import { getAccessToken } from '@/shared/auth/token';

// Same-origin API base. The axios baseURL is '/api/v1/goodday', so we use
// relative paths that axios will resolve correctly.
// For the SSE stream we use fetch directly, constructing the full path.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const API_PATH = `${API_BASE}/api/v1/goodday`;
const MAX_LOGS = 400;
const MAX_LEADS = 600;

export interface ScrapeStreamState {
  status: StreamStatus;
  message: string;
  logs: ScrapeLogEntry[];
  leads: ScrapeLead[];
  savedCount: number;
  dupCount: number;
  elapsedSec: number;
}

const INITIAL: ScrapeStreamState = {
  status: 'idle',
  message: '',
  logs: [],
  leads: [],
  savedCount: 0,
  dupCount: 0,
  elapsedSec: 0,
};

/**
 * fetch + ReadableStream-based scrape runner. The live stream is RUNTIME state (not React
 * Query). React Query is invalidated EXACTLY ONCE on a terminal state
 * (complete / stopped / error) so persistent job history refreshes.
 *
 * Streaming-stability guarantees:
 *  - Never auto-reconnects (a one-shot scrape must not restart): we close() on
 *    every terminal event and treat any onerror as terminal.
 *  - A `terminal` ref guards against double-finalisation / duplicate invalidations.
 *  - The connection + timer are always torn down on unmount.
 */
export function useScrapeStream() {
  const queryClient = useQueryClient();
  const controllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef(0);
  const logIdRef = useRef(0);
  const terminalRef = useRef(false);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const [state, setState] = useState<ScrapeStreamState>(INITIAL);

  const teardown = useCallback(() => {
    if (readerRef.current) {
      try { readerRef.current.cancel() } catch { /* ignore */ }
      readerRef.current = null;
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finalize = useCallback(
    (status: StreamStatus, message: string, invalidateDelayMs = 0) => {
      if (terminalRef.current) return;
      terminalRef.current = true;
      teardown();
      setState((s) => ({ ...s, status, message: message || s.message }));
      if (invalidateDelayMs > 0) {
        settleRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: JOBS_KEY }), invalidateDelayMs);
      } else {
        queryClient.invalidateQueries({ queryKey: JOBS_KEY });
      }
    },
    [queryClient, teardown],
  );

  const readStream = useCallback(async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) {
      finalize('error', 'The scrape stream is not supported in this browser.');
      return;
    }
    readerRef.current = reader;
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;

          let data: { type?: string; [k: string]: unknown };
          try {
            data = JSON.parse(payload);
          } catch {
            continue;
          }
          switch (data.type) {
            case 'status':
              setState((s) => ({ ...s, status: 'running', message: String(data.message ?? '') }));
              break;
            case 'log': {
              const log = (data.log ?? {}) as { status?: string; message?: string };
              const entry: ScrapeLogEntry = { id: ++logIdRef.current, status: log.status ?? 'info', message: log.message ?? '' };
              const isDup = /^Duplicate skipped/i.test(entry.message);
              setState((s) => ({
                ...s,
                logs: [...s.logs.slice(-(MAX_LOGS - 1)), entry],
                dupCount: isDup ? s.dupCount + 1 : s.dupCount,
              }));
              break;
            }
            case 'lead': {
              const lead = data.lead as ScrapeLead;
              setState((s) => ({ ...s, leads: [lead, ...s.leads].slice(0, MAX_LEADS), savedCount: s.savedCount + 1 }));
              break;
            }
            case 'complete':
              setState((s) => ({
                ...s,
                savedCount: typeof data.saved === 'number' ? data.saved : s.savedCount,
                dupCount: typeof data.duplicates === 'number' ? data.duplicates : s.dupCount,
              }));
              finalize('completed', `Completed — ${data.saved ?? 0} saved, ${data.duplicates ?? 0} duplicate(s).`);
              return;
            case 'error':
              finalize('error', String(data.message ?? 'The scrape failed.'));
              return;
          }
        }
      }

      const s = state;
      finalize('completed', `Completed — ${s.savedCount} saved, ${s.dupCount} duplicate(s).`);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        finalize('stopped', 'Scan stopped.');
      } else {
        finalize('error', 'The scrape connection was lost.');
      }
    } finally {
      readerRef.current = null;
    }
  }, [finalize, state]);

  const start = useCallback(
    (params: ScrapeParams) => {
      if (controllerRef.current) return;
      terminalRef.current = false;
      logIdRef.current = 0;
      startedAtRef.current = Date.now();
      setState({ ...INITIAL, status: 'connecting' });

      const qs = new URLSearchParams({
        niche: params.niche,
        location: params.location,
        minRating: String(params.minRating),
        maxResults: String(params.maxResults),
        minReviews: String(params.minReviews),
        websiteFilter: params.websiteFilter,
        ageFilter: params.ageFilter,
        requirePhone: params.requirePhone ? '1' : '0',
        requireEmail: params.requireEmail ? '1' : '0',
        requireAddress: params.requireAddress ? '1' : '0',
      }).toString();

      const controller = new AbortController();
      controllerRef.current = controller;

      const token = getAccessToken();
      if (!token) {
        finalize('error', 'You are not authenticated. Please log in again.');
        return;
      }
      const url = `${API_PATH}/scrape?${qs}`;

      fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              let msg = `Server returned ${response.status}`;
              try { const j = JSON.parse(text); msg = j.error || j.message || msg } catch { /* use status */ }
              throw new Error(msg);
            });
          }
          return response;
        })
        .then((response) => readStream(response))
        .catch((err: any) => {
          if (err.name === 'AbortError') return;
          setState((s) => {
            const neverStarted = s.logs.length === 0 && s.savedCount === 0;
            const msg = neverStarted
              ? `Couldn't start the scan stream: ${err.message || 'A previous scan may still be finishing — wait a few seconds and try again.'}`
              : 'The scrape connection was lost.';
            return { ...s, message: msg };
          });
          finalize('error', '');
        });

      timerRef.current = setInterval(() => {
        setState((s) => ({ ...s, elapsedSec: Math.round((Date.now() - startedAtRef.current) / 1000) }));
      }, 1000);
    },
    [finalize, readStream],
  );

  const stop = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    finalize('stopped', 'Scan stopped.', 800);
  }, [finalize]);

  const reset = useCallback(() => {
    if (controllerRef.current) return;
    setState(INITIAL);
  }, []);

  useEffect(() => {
    return () => {
      teardown();
      if (settleRef.current) clearTimeout(settleRef.current);
    };
  }, [teardown]);

  const isActive = state.status === 'connecting' || state.status === 'running';

  return { ...state, isActive, start, stop, reset };
}